"use client";

import { useRef } from "react";
import NextImage from "next/image";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";

import { useIsMobile } from "@/hooks/useIsMobile";
import { GROQImage } from "@/sanity/lib/definitions";
import { PageEventSchema } from "@/sanity/pages/PageEventTemplate";
import { TicketCTA, gridTile, wave } from "@/components/pageComponents/event/EventShared";

/** Dark image panel with scroll parallax. */
function ParallaxImagePanel({ image }: { image?: GROQImage }) {
  const isMobile = useIsMobile();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // The cutout statue travels ~16% of its own height across the scroll (percent keeps it proportional on wide screens).
  const rawY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const y = useSpring(rawY, { stiffness: 120, damping: 30, mass: 0.5 });

  return (
    <div
      ref={ref}
      className="relative h-[54.75rem] w-full overflow-clip border border-[#666] bg-[#143f33] lg:h-auto lg:w-[71.125rem] lg:self-stretch">
      {/* Glowing chart line */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-[33.25rem] left-[-52rem] h-[156.25rem] w-[271.875rem] lg:top-[44.75rem] lg:left-[-41.375rem]">
        <div className="absolute inset-[-47.12%_-27.07%]">
          <NextImage
            src={wave}
            alt=""
            className="block !h-full !w-full !max-w-none"
          />
        </div>
      </div>
      {/* Dotted grid texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.23]"
        style={{
          backgroundImage: `url(${gridTile.src})`,
          backgroundSize: "3.5rem 3.5rem",
          backgroundPosition: "top left",
        }}
      />
      {/* Parallax image — exact Figma box (680.4×853 desktop, 406×508 mobile) */}
      {image?.src && (
        <motion.div
          style={isMobile === false ? { y } : undefined}
          className="absolute top-[2.5rem] left-[-1.625rem] h-[63.5rem] w-[50.75rem] lg:top-[5.75rem] lg:left-[-8.525rem] lg:h-[106.625rem] lg:w-[85.05rem]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={image.src}
            alt=""
            className="h-full w-full object-cover object-top"
          />
        </motion.div>
      )}
    </div>
  );
}

/** One numbered reason. Opacity is scroll-linked: dimmed as the row enters the viewport, fully lit by the time it reaches ~55% of the viewport height. */
function Reason({ index, text }: { index: number; text?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 95%", "start 55%"],
  });
  const rowOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 1]);
  const numberOpacity = useTransform(scrollYProgress, [0, 1], [0.15, 1]);

  return (
    <motion.div
      ref={ref}
      style={{ opacity: rowOpacity }}
      className="flex w-full flex-col items-start gap-[3.75rem] lg:flex-row lg:gap-[6.625rem]">
      <motion.p
        style={{ opacity: numberOpacity }}
        className="mb-0 w-[7.75rem] shrink-0 font-blacker text-[6.375rem] leading-[1.1] font-normal tracking-[-0.445rem] text-[#001b14]">
        {String(index + 1).padStart(2, "0")}
      </motion.p>
      <p className="mr-[2rem] mb-0 flex-1 font-geist text-[2.25rem] leading-[1.3] font-normal tracking-[-0.045rem] text-black lg:mr-[4rem]">
        {text}
      </p>
    </motion.div>
  );
}

/** Why attend section: parallax statue panel + numbered reasons card. */
export function EventWhyAttend({ data }: { data: PageEventSchema }) {
  const reasons = data.s3Items ?? [];

  return (
    <section className="flex w-full flex-col items-center bg-white px-[2rem] pb-[8rem] lg:px-0 lg:pb-[15rem]">
      <div className="flex w-full flex-col items-center gap-[2.5rem] lg:w-[151rem] lg:gap-[3.875rem] lg:flex-row lg:items-stretch">
        <ParallaxImagePanel image={data.s3Image} />

        <div className="flex w-full flex-col gap-[1.25rem] border border-[#cdcdcd] bg-[rgba(246,246,246,0.65)] p-[2rem] backdrop-blur-[2.75rem] lg:w-[76rem] lg:px-[4.5rem] lg:py-[5rem]">
          <div className="flex flex-col gap-[3rem] lg:gap-[2.25rem]">
            <div className="flex flex-col gap-[3rem]">
              {data.s3BadgeText && (
                <div className="flex w-fit items-center gap-[1.25rem] border border-black/10 bg-black/[0.04] px-[2.5rem] py-[1.5rem]">
                  <span className="h-[0.75rem] w-[0.75rem] rounded-full bg-[#1b8869]" />
                  <p className="mb-0 font-geist text-[1.75rem] leading-[1.15] font-medium whitespace-nowrap text-black/[0.64] uppercase">
                    {data.s3BadgeText}
                  </p>
                </div>
              )}
              <div className="mb-[1rem] flex flex-col gap-[2.5rem] lg:mb-[3rem] lg:gap-0">
                {(data.s3TitleRegular || data.s3TitleItalic) && (
                  <h2 className="mb-0 w-[37.25rem] font-blacker text-[4.5rem] leading-[1.1] font-normal tracking-[-0.135rem] text-black lg:w-[51.5rem] lg:text-[6rem] lg:tracking-[-0.18rem] [&_span]:font-blacker">
                    {data.s3TitleRegular && <span>{data.s3TitleRegular} </span>}
                    {data.s3TitleItalic && <span className="text-[#001b14] italic">{data.s3TitleItalic}</span>}
                  </h2>
                )}
                {data.s3Description && (
                  <p className="mb-0 w-full font-geist text-[2.25rem] leading-[1.5] font-normal text-black/70 lg:w-[63.875rem]">
                    {data.s3Description}
                  </p>
                )}
              </div>
            </div>

            {!!reasons.length && (
              <div className="flex w-full flex-col items-center justify-center gap-[4rem] lg:w-[70.75rem]">
                {reasons.map((reason, i) => (
                  <div
                    key={reason._key ?? i}
                    className="contents">
                    {i > 0 && <div className="w-full border-t border-black/10" />}
                    <Reason
                      index={i}
                      text={reason.text}
                    />
                  </div>
                ))}
              </div>
            )}

            <TicketCTA
              data={data.s3Cta}
              className="mt-[1rem] h-[7.4rem] w-[45.5rem] lg:h-[7.75rem] lg:w-[47.625rem]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
